// ============================================================
// Global Error Boundary
// ============================================================

import { Component } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { tokens } from '@/theme/tokens';

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: tokens.colors.background,
            px: 3,
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: tokens.radius.full,
              backgroundColor: `${tokens.colors.error.main}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <AlertTriangle size={40} color={tokens.colors.error.main} />
          </Box>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
            Something went wrong
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 480 }}
          >
            An unexpected error occurred. Please refresh the page or contact
            support if the problem persists.
          </Typography>
          <Button
            variant="contained"
            startIcon={<RefreshCw size={18} />}
            onClick={this.handleReset}
          >
            Refresh Page
          </Button>
          {import.meta.env.DEV && this.state.error && (
            <Box
              sx={{
                mt: 4,
                p: 2,
                maxWidth: 600,
                width: '100%',
                backgroundColor: tokens.colors.grey[50],
                borderRadius: tokens.radius.md,
                textAlign: 'left',
                overflow: 'auto',
              }}
            >
              <Typography
                variant="caption"
                component="pre"
                sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
              >
                {this.state.error.toString()}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}
